// OGC-531 Creatives Manager v19 save: edits become persisted changes, never
// in-place edits of a saved version.
// - Details (name, internal label, description, display order) update the
//   creative; a renovation-type change reassigns it (server: only while no
//   campaign uses it).
// - Changed content (copy or media) is saved as a NEW immutable Draft
//   revision. Media you keep is copied server-side from the revision you
//   edited into the new revision's own storage; new files are uploaded.
// Each attempt keeps its ids and the steps already done, so a retry after a
// failure replays the same uploads, copies and revision instead of creating
// duplicates.
import { attemptKey, makeUuid } from './creatives-api.js';

export const VIDEO_MAX_BYTES = 512 * 1024 * 1024;
export const POSTER_MAX_BYTES = 10 * 1024 * 1024;
const POSTER_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp']);

export function posterContentType(file) {
  const declared = String(file?.type || '').toLowerCase();
  if (POSTER_TYPES.has(declared)) return declared;
  const name = String(file?.name || '').toLowerCase();
  if (name.endsWith('.jpg') || name.endsWith('.jpeg')) return 'image/jpeg';
  if (name.endsWith('.png')) return 'image/png';
  if (name.endsWith('.webp')) return 'image/webp';
  return '';
}

export function mediaFileError(file, kind) {
  if (!file) return '';
  if (kind === 'video') {
    const type = String(file.type || '').toLowerCase();
    if ((type && type !== 'video/mp4') || !String(file.name || '').toLowerCase().endsWith('.mp4')) return 'Choose an MP4 video.';
    if (Number(file.size || 0) > VIDEO_MAX_BYTES) return 'Choose an MP4 under 512 MB.';
    return '';
  }
  if (!posterContentType(file)) return 'Choose a JPG, PNG or WebP poster.';
  if (Number(file.size || 0) > POSTER_MAX_BYTES) return 'Choose a poster under 10 MB.';
  return '';
}

function safeFilename(value, fallback) {
  return String(value || '').replace(/[\r\n\u0000-\u001f\u007f]/g, '').replace(/[^a-zA-Z0-9._ ()-]/g, '_').slice(0, 180) || fallback;
}

// A readable, unique creative key: the label's words plus the creative id's
// first characters (keys are unique across archived creatives too).
export function packageKeyFrom(value, packageId) {
  const words = String(value || '')
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60)
    .replace(/-+$/, '');
  return `${words || 'creative'}-${String(packageId).slice(0, 8)}`;
}

export function uploadHeaders({ artifactId, posterArtifactId = null, generatePoster = false, file, kind }) {
  return {
    'Content-Type': kind === 'video' ? 'video/mp4' : posterContentType(file),
    'Idempotency-Key': `catalog-${kind}-${artifactId}`,
    'X-Catalog-Artifact-Id': artifactId,
    ...(posterArtifactId ? { 'X-Catalog-Poster-Artifact-Id': posterArtifactId } : {}),
    ...(generatePoster ? { 'X-Catalog-Generate-Poster': 'true' } : {}),
    'X-Catalog-Filename': safeFilename(file.name, kind === 'video' ? 'creative.mp4' : 'poster.jpg'),
  };
}

const text = (value) => String(value ?? '').trim();
// Single-line creative text: the database refuses control characters such
// as line breaks in the description.
const line = (value) => text(value).replace(/[\u0000-\u001f\u007f]+/g, ' ');

export function detailsFrom(pkg = {}) {
  return {
    displayName: text(pkg.displayName),
    internalLabel: text(pkg.internalLabel),
    description: text(pkg.description),
    displayOrder: Number.isSafeInteger(Number(pkg.displayOrder)) ? Number(pkg.displayOrder) : 1,
  };
}

export function contentFrom(version = {}) {
  return {
    primaryText: text(version.primaryText),
    headline: text(version.headline),
    callToAction: text(version.callToAction) || 'LEARN_MORE',
  };
}

// What a save would change, from the editor values and the saved creative.
export function savePlan({ values, pkg = null, base = null }) {
  const details = {
    displayName: text(values.displayName),
    internalLabel: text(values.internalLabel) || text(values.displayName),
    description: line(values.description),
    displayOrder: Number(values.displayOrder),
  };
  const content = {
    primaryText: text(values.primaryText),
    headline: text(values.headline),
    callToAction: text(values.callToAction).toUpperCase() || 'LEARN_MORE',
  };
  if (!pkg) {
    return { create: true, details, content, renovationTypeId: values.renovationTypeId, detailsChanged: true, typeChanged: false, contentChanged: true };
  }
  const savedDetails = detailsFrom(pkg);
  const savedContent = contentFrom(base || {});
  const detailsChanged = Object.keys(details).some((key) => details[key] !== savedDetails[key]);
  const typeChanged = values.renovationTypeId !== pkg.renovationTypeId;
  const contentChanged = !base
    || Boolean(values.videoFile || values.posterFile)
    || Object.keys(content).some((key) => content[key] !== savedContent[key]);
  return { create: false, details, content, renovationTypeId: values.renovationTypeId, detailsChanged, typeChanged, contentChanged };
}

// The first problem that stops a save, with the tab and field it belongs to.
export function saveProblem({ values, base = null, types = [] }) {
  if (!text(values.displayName)) return { tab: 'details', field: 'displayName', message: 'Enter a creative name.' };
  if (!values.renovationTypeId) return { tab: 'details', field: 'renovationTypeId', message: 'Choose a renovation type.' };
  if (types.find((type) => type.id === values.renovationTypeId)?.archivedAt) return { tab: 'details', field: 'renovationTypeId', message: 'Choose an active renovation type.' };
  if (!Number.isSafeInteger(Number(values.displayOrder)) || Number(values.displayOrder) < 0 || Number(values.displayOrder) > 1000000 || values.displayOrder === '') return { tab: 'details', field: 'displayOrder', message: 'Display order must be a whole number from 0 to 1,000,000.' };
  if (!values.videoFile && !base?.videoAssetId) return { tab: 'media', field: 'video', message: 'Add a video before saving.' };
  const videoError = mediaFileError(values.videoFile, 'video');
  if (videoError) return { tab: 'media', field: 'video', message: videoError };
  const posterError = mediaFileError(values.posterFile, 'poster');
  if (posterError) return { tab: 'media', field: 'poster', message: posterError };
  if (!values.posterFile && !values.videoFile && !base?.posterAssetId) return { tab: 'media', field: 'poster', message: 'Add a poster before saving.' };
  if (!text(values.primaryText)) return { tab: 'media', field: 'primaryText', message: 'Add the primary text.' };
  if (!text(values.headline)) return { tab: 'media', field: 'headline', message: 'Add a headline.' };
  return null;
}

const fileIdentity = (value) => (value ? `${value.name}:${value.size}:${value.lastModified || 0}` : '');

// A signature of the values a save writes; a changed signature starts a new
// attempt (fresh ids), an unchanged one retries the same attempt.
export function attemptSignature(values) {
  return JSON.stringify([
    // A blank internal label saves as the creative name (see savePlan).
    text(values.displayName), text(values.internalLabel) || text(values.displayName), line(values.description), String(values.displayOrder),
    values.renovationTypeId, text(values.primaryText), text(values.headline), text(values.callToAction),
    fileIdentity(values.videoFile), fileIdentity(values.posterFile),
  ]);
}

// Where each media file of an attempt comes from: a picked file, a poster
// generated from the picked video, or a copy of the saved revision's.
function mediaSources(values) {
  return {
    video: values.videoFile ? `file:${fileIdentity(values.videoFile)}` : 'copy',
    poster: values.posterFile ? `file:${fileIdentity(values.posterFile)}` : values.videoFile ? `generated:${fileIdentity(values.videoFile)}` : 'copy',
  };
}

// A new attempt for changed values. Media already stored by an unfinished
// `previous` attempt is reused (same revision and artifact ids) when its
// source is unchanged, so editing after a partial failure never re-uploads
// the same bytes under a new id (the server refuses duplicate checksums).
export function newAttempt({ values, packageId = null, previous = null }) {
  const id = packageId || makeUuid();
  const attempt = {
    signature: attemptSignature(values),
    media: mediaSources(values),
    packageId: id,
    versionId: makeUuid(),
    videoAssetId: makeUuid(),
    posterAssetId: makeUuid(),
    keys: {
      package: `catalog-package-${id}`,
      details: attemptKey('package-details', id),
      type: attemptKey('package-type', id),
      copy: attemptKey('asset-copy', id),
      version: null,
    },
    done: {},
  };
  if (previous && previous.packageId === id && !previous.done.version) {
    attempt.versionId = previous.versionId;
    attempt.keys.copy = previous.keys.copy;
    if (previous.done.package) attempt.done.package = true;
    for (const kind of ['video', 'poster']) {
      if (previous.media?.[kind] !== attempt.media[kind]) continue;
      attempt[`${kind}AssetId`] = previous[`${kind}AssetId`];
      if (previous.done[kind]) attempt.done[kind] = true;
    }
  }
  return attempt;
}

// Runs the remaining steps of an attempt. `onStep` reports progress text;
// the attempt's `done` map records finished steps for a retry.
export async function runSave({ apiClient, attempt, values, pkg, base, onStep = () => {} }) {
  const plan = savePlan({ values, pkg, base });
  const done = attempt.done;
  const packageId = attempt.packageId;
  if (plan.create && !done.package) {
    onStep('Creating the draft creative…');
    await apiClient.createPackage({
      id: packageId,
      renovationTypeId: plan.renovationTypeId,
      packageKey: packageKeyFrom(plan.details.internalLabel, packageId),
      internalLabel: plan.details.internalLabel,
      displayName: plan.details.displayName,
      description: plan.details.description || undefined,
      displayOrder: plan.details.displayOrder,
    }, attempt.keys.package);
    done.package = true;
  }
  if (!plan.create && plan.detailsChanged && !done.details) {
    onStep('Saving creative details…');
    await apiClient.updatePackageDetails(packageId, {
      displayName: plan.details.displayName,
      internalLabel: plan.details.internalLabel,
      description: plan.details.description,
      displayOrder: plan.details.displayOrder,
    }, attempt.keys.details);
    done.details = true;
  }
  if (!plan.create && plan.typeChanged && !done.type) {
    onStep('Changing the renovation type…');
    await apiClient.reassignPackageType(packageId, plan.renovationTypeId, attempt.keys.type);
    done.type = true;
  }
  if (!plan.contentChanged) return { versionCreated: false, plan };

  const versionId = attempt.versionId;
  if (values.posterFile && !done.poster) {
    onStep('Uploading the poster…');
    await apiClient.uploadAsset(packageId, versionId, 'poster', values.posterFile, uploadHeaders({ artifactId: attempt.posterAssetId, file: values.posterFile, kind: 'poster' }));
    done.poster = true;
  }
  if (values.videoFile && !done.video) {
    const generatePoster = !values.posterFile;
    onStep(generatePoster ? 'Uploading and checking the video, then capturing a poster…' : 'Uploading and checking the video…');
    await apiClient.uploadAsset(packageId, versionId, 'video', values.videoFile, uploadHeaders({
      artifactId: attempt.videoAssetId,
      posterArtifactId: generatePoster ? attempt.posterAssetId : null,
      generatePoster,
      file: values.videoFile,
      kind: 'video',
    }));
    done.video = true;
    if (generatePoster) done.poster = true;
  }
  if (!values.videoFile && !done.video) {
    onStep('Keeping the saved video…');
    await apiClient.copyAsset(packageId, versionId, 'video', { sourceVersionId: base.id, artifactId: attempt.videoAssetId }, `${attempt.keys.copy}-video`);
    done.video = true;
  }
  if (!values.posterFile && !done.poster) {
    onStep('Keeping the saved poster…');
    await apiClient.copyAsset(packageId, versionId, 'poster', { sourceVersionId: base.id, artifactId: attempt.posterAssetId }, `${attempt.keys.copy}-poster`);
    done.poster = true;
  }
  onStep('Saving the new draft revision…');
  const version = await apiClient.createVersion(packageId, {
    id: versionId,
    videoAssetId: attempt.videoAssetId,
    posterAssetId: attempt.posterAssetId,
    primaryText: plan.content.primaryText,
    headline: plan.content.headline,
    callToAction: plan.content.callToAction,
  }, `catalog-version-${versionId}`);
  done.version = true;
  return { versionCreated: true, version, plan };
}
